import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Music, Calendar, ArrowLeft, RefreshCw, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials for cookie-based auth
const authAxios = axios.create({
  withCredentials: true
});

const AvailabilityManager = () => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const verifyAuth = useCallback(async () => {
    try {
      await authAxios.get(`${API}/auth/verify`);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      window.location.href = '/admin';
      return false;
    }
  }, []);

  const fetchTimeSlots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authAxios.get(`${API}/timeslots`);
      setTimeSlots(response.data);
    } catch (error) {
      toast.error('Failed to load time slots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await verifyAuth();
      if (isAuth) {
        fetchTimeSlots();
      }
    };
    init();
  }, [verifyAuth, fetchTimeSlots]);

  const toggleAvailability = async (day, time, currentStatus) => {
    setUpdating(true);
    try {
      await authAxios.put(`${API}/timeslots`, {
        day,
        time,
        isAvailable: !currentStatus
      });
      
      // Update local state
      setTimeSlots(timeSlots.map(slot => 
        slot.day === day && slot.time === time 
          ? { ...slot, isAvailable: !currentStatus }
          : slot
      ));
      
      toast.success(!currentStatus ? 'Slot marked as available' : 'Slot marked as booked');
    } catch (error) {
      toast.error('Failed to update time slot');
    } finally {
      setUpdating(false);
    }
  };

  const resetTimeSlots = async () => {
    if (!window.confirm('Are you sure you want to reset all time slots to the default schedule? This will remove all current slots and create new ones.')) {
      return;
    }
    
    setResetting(true);
    try {
      await authAxios.post(`${API}/admin/timeslots/reset`);
      toast.success('Time slots reset successfully!');
      fetchTimeSlots();
    } catch (error) {
      toast.error('Failed to reset time slots');
    } finally {
      setResetting(false);
    }
  };

  // Function to parse time string and convert to minutes for sorting
  const parseTimeToMinutes = (timeStr) => {
    const startTime = timeStr.split('-')[0].trim();
    
    // Check if the START time specifically has am/pm, or infer from end time
    const startHasAM = startTime.toLowerCase().includes('am');
    const startHasPM = startTime.toLowerCase().includes('pm');
    
    // If start time doesn't have am/pm marker, check the end time
    const endTime = timeStr.split('-')[1]?.trim() || '';
    const endHasPM = endTime.toLowerCase().includes('pm');
    
    const timePart = startTime.replace(/\s*(am|pm)/i, '').trim();
    const [hours, minutes] = timePart.split(':').map(Number);
    
    let hour24 = hours;
    
    // Determine if start time is AM or PM
    let isStartPM = startHasPM;
    let isStartAM = startHasAM;
    
    // If start has no marker, infer from context
    if (!startHasAM && !startHasPM) {
      if (endHasPM) {
        const endTimePart = endTime.replace(/\s*(am|pm)/i, '').trim();
        const [endHours] = endTimePart.split(':').map(Number);
        
        // 11:15-12:00 pm -> 11 is AM (11 < 12)
        // 12:00-12:45 pm -> 12 is PM
        if (hours === 11 && endHours === 12) {
          isStartAM = true;
        } else if (hours === 12) {
          isStartPM = true;
        } else {
          isStartPM = true;
        }
      }
    }
    
    if (isStartPM && hours !== 12) {
      hour24 = hours + 12;
    } else if (isStartAM && hours === 12) {
      hour24 = 0;
    }
    
    return hour24 * 60 + (minutes || 0);
  };

  const groupedSlots = timeSlots.reduce((acc, slot) => {
    if (!acc[slot.day]) {
      acc[slot.day] = [];
    }
    acc[slot.day].push(slot);
    return acc;
  }, {});

  // Sort each day's slots by time (early to late)
  Object.keys(groupedSlots).forEach(day => {
    groupedSlots[day].sort((a, b) => parseTimeToMinutes(a.time) - parseTimeToMinutes(b.time));
  });

  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Saturday'];

  // Show loading while checking auth
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-100">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Music className="w-8 h-8 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Manage Availability</h1>
              <p className="text-sm text-gray-600">Toggle time slots available for booking</p>
            </div>
          </div>
          <Button 
            onClick={() => window.location.href = '/admin'}
            variant="outline" 
            className="border-amber-600 text-amber-600 hover:bg-amber-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </nav>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <p className="text-gray-600">
              Green = Available for booking | Gray = Already booked/unavailable
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Toggle switches to mark slots as available or booked. Changes appear immediately on your booking form.
            </p>
          </div>
          <Button 
            onClick={resetTimeSlots}
            disabled={resetting}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
            {resetting ? 'Resetting...' : 'Reset Time Slots'}
          </Button>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600">Loading availability...</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {dayOrder.map(day => (
              groupedSlots[day] && (
                <Card key={day} className="border-amber-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-amber-600" />
                      {day}
                    </CardTitle>
                    <CardDescription>
                      {groupedSlots[day].filter(s => s.isAvailable).length} of {groupedSlots[day].length} slots available
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {groupedSlots[day].map(slot => (
                        <div 
                          key={`${slot.day}-${slot.time}`}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            slot.isAvailable 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <span className={`font-medium ${
                            slot.isAvailable ? 'text-green-800' : 'text-gray-500'
                          }`}>
                            {slot.time}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              {slot.isAvailable ? 'Available' : 'Booked'}
                            </span>
                            <Switch
                              checked={slot.isAvailable}
                              onCheckedChange={() => toggleAvailability(slot.day, slot.time, slot.isAvailable)}
                              disabled={updating}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )
            ))}
          </div>
        )}

        <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">💡 Tips:</h3>
          <ul className="space-y-1 text-sm text-gray-700">
            <li>• Update this weekly after checking your MyMusicStaff schedule</li>
            <li>• Only available slots will show on the booking form</li>
            <li>• Changes are instant - no need to save or publish</li>
            <li>• Toggle back to available when a slot opens up</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityManager;
